import {apiSlice} from './apiSlice';

/**
 * Endpoints tool Google Analytics 4 (bên trong một Project) — nhân bản gscApiSlice.
 *
 * Kết nối/OAuth: getGaAuthUrl → connectGa (đổi code, credential dùng chung gsc_accounts)
 * → selectGaProperty (property GA4, khởi động sync nền). getGaConnection cho trạng thái
 * + tiến độ (poll khi đang backfill). Báo cáo phục vụ TỪ DB (overview + channel/source/
 * page/country/device). Tag 'Ga' theo id `project-<id>`.
 */
export const gaApiSlice = apiSlice.injectEndpoints({
	endpoints: (builder) => ({
		getGaConnection: builder.query({
			query: (projectId) => ({url: `ga/projects/${projectId}/connection`, method: 'get'}),
			transformResponse: (body) => body?.data || null,
			providesTags: (result, error, projectId) => [{type: 'Ga', id: `project-${projectId}`}],
		}),
		// Tài khoản Google đã kết nối (dùng chung với GSC) — has_analytics = dùng lại được cho GA4.
		getGaAccounts: builder.query({
			query: () => ({url: 'ga/accounts', method: 'get'}),
			transformResponse: (body) => body?.data?.accounts || [],
			providesTags: [{type: 'Ga', id: 'accounts'}],
		}),
		useGaAccount: builder.mutation({
			query: ({projectId, account_id}) => ({url: `ga/projects/${projectId}/use-account`, method: 'post', data: {account_id}}),
			transformResponse: (body) => body?.data || null,
			invalidatesTags: (result, error, {projectId}) => [{type: 'Ga', id: `project-${projectId}`}],
		}),
		getGaAuthUrl: builder.mutation({
			query: (projectId) => ({url: `ga/projects/${projectId}/auth-url`, method: 'get'}),
			transformResponse: (body) => body?.data || null,
		}),
		connectGa: builder.mutation({
			query: ({projectId, code, state}) => ({url: `ga/projects/${projectId}/connect`, method: 'post', data: {code, state}}),
			transformResponse: (body) => body?.data || null,
			invalidatesTags: (result, error, {projectId}) => [{type: 'Ga', id: `project-${projectId}`}, {type: 'Ga', id: 'accounts'}],
		}),
		getGaProperties: builder.query({
			query: (projectId) => ({url: `ga/projects/${projectId}/properties`, method: 'get'}),
			transformResponse: (body) => body?.data?.properties || [],
		}),
		selectGaProperty: builder.mutation({
			query: ({projectId, property, property_name}) => ({
				url: `ga/projects/${projectId}/property`,
				method: 'post',
				data: {property, ...(property_name ? {property_name} : {})},
			}),
			transformResponse: (body) => body?.data || null,
			invalidatesTags: (result, error, {projectId}) => [{type: 'Ga', id: `project-${projectId}`}],
		}),
		disconnectGa: builder.mutation({
			query: (projectId) => ({url: `ga/projects/${projectId}/connection`, method: 'delete'}),
			invalidatesTags: (result, error, projectId) => [{type: 'Ga', id: `project-${projectId}`}, {type: 'Ga', id: 'accounts'}],
		}),
		getGaOverview: builder.query({
			query: ({projectId, from, to}) => ({
				url: `ga/projects/${projectId}/overview`,
				method: 'get',
				...(from || to ? {params: {...(from ? {from} : {}), ...(to ? {to} : {})}} : {}),
			}),
			transformResponse: (body) => body?.data || null,
			providesTags: (result, error, {projectId}) => [{type: 'Ga', id: `overview-${projectId}`}],
		}),
		// Báo cáo 1 chiều dùng chung: dim = channels | sources | pages | countries | devices.
		getGaDimension: builder.query({
			query: ({projectId, dim, from, to, limit = 50}) => ({
				url: `ga/projects/${projectId}/${dim}`,
				method: 'get',
				params: {...(from ? {from} : {}), ...(to ? {to} : {}), limit},
			}),
			transformResponse: (body) => body?.data || {rows: [], metric: 'sessions'},
		}),
	}),
});

export const {
	useGetGaConnectionQuery,
	useGetGaAccountsQuery,
	useUseGaAccountMutation,
	useGetGaAuthUrlMutation,
	useConnectGaMutation,
	useGetGaPropertiesQuery,
	useSelectGaPropertyMutation,
	useDisconnectGaMutation,
	useGetGaOverviewQuery,
	useGetGaDimensionQuery,
} = gaApiSlice;
